/* eslint-disable react/jsx-props-no-spreading */
import React, { useEffect, useEffectEvent, useId } from 'react';
import { twMerge } from 'utilities/src/twMerge';

import { IInputDropdownProps } from './_props';

import { InputStateEnum } from '../../shared/utils/state';
import { TFormField } from '../../shared/utils/types';

import { LabelTop, MessageError, MessageHelper, Field, Options } from '../../shared/components';

import { stateClasses, inputDropdownClasses } from './_style';

import { useDropdown } from './hooks/useDropdown';

function InputDropdown<TFormFields extends TFormField = TFormField>({
  id,
  name,
  options,
  size = 'Medium',
  label = '',
  placeholder = '',
  isSelect = false,
  labelPosition = 'Top',
  errorMessage = '',
  errorIcon = undefined,
  errorClassName = '',
  helperMessage = '',
  state = 'Default',
  theme = 'Light',
  variant = 'Secondary',
  icon = undefined,
  iconName,
  iconPosition = 'left',
  required = false,
  className = '',
  fieldClassName = '',
  optionsClassName = '',
  optionsContainerHeight,
  filterable = true,
  register,
  rules,
  onChangeText,
  onChangeSelect,
  onOpenChange,
  onKeyDown,
  searchRef,
  highlightOptions,
  highlightHelperText,
  maxLength,
  defaultValue,
  errorSpace = false,
  searchRules,
}: IInputDropdownProps<TFormFields>) {
  const dropdownId = useId();

  const {
    isOpen,
    getInputProps,
    getItemProps,
    getLabelProps,
    getMenuProps,
    highlightedIndex,
    inputValue,
    selectedItem,
    listOptions,
    getToggleButtonProps,
  } = useDropdown({
    isSelect,
    options,
    filterable,
    onChangeSelect,
    onChangeText,
    defaultValue,
    id: id || dropdownId,
    searchRules,
  });

  const { id: labelId, htmlFor, ...labelProps } = getLabelProps();

  const notifyOpenChange = useEffectEvent((open: boolean) => {
    onOpenChange?.(open);
  });

  useEffect(() => {
    notifyOpenChange(isOpen);
  }, [isOpen]);

  return (
    <div className={twMerge(inputDropdownClasses, stateClasses.get(InputStateEnum[state]), className)}>
      <label htmlFor={htmlFor} {...labelProps} className={twMerge({ 'select-none': !filterable })}>
        <LabelTop
          size={size}
          label={label}
          labelPosition={labelPosition}
          theme={theme}
          required={required}
          id={labelId}
        />

        <div className="relative">
          <Field
            size={size}
            type="Input"
            state={state}
            theme={theme}
            variant={variant}
            label={label}
            labelPosition={labelPosition}
            value={inputValue}
            icon={selectedItem?.icon || icon}
            stateIcon={iconName}
            iconPosition={iconPosition}
            required={required}
            className={fieldClassName}
            id={labelId}
            {...labelProps}
            getToggleButtonProps={getToggleButtonProps}
            isOpen={isOpen}
          >
            <input
              type="text"
              maxLength={maxLength}
              className={twMerge('select-all border-none focus:outline-none', {
                'pointer-events-none w-full': isSelect,
              })}
              name={name}
              data-testid={name}
              {...(labelPosition === 'Top' && { placeholder })}
              {...getInputProps({
                'aria-autocomplete': 'both',
                onKeyDown,
                ref: searchRef,
                ...{
                  ...(register &&
                    register(name, {
                      ...rules,
                    })),
                },
                readOnly: isSelect,
              })}
            />
          </Field>

          <Options
            open={isOpen}
            options={listOptions}
            className={optionsClassName}
            containerHeight={optionsContainerHeight}
            valueText={inputValue}
            iconPosition={iconPosition}
            highlightOptions={highlightOptions}
            highlightHelperText={highlightHelperText}
            getMenuProps={getMenuProps}
            getItemProps={getItemProps}
            hoveredItemIndex={highlightedIndex}
            selectedItem={selectedItem}
          />
        </div>
      </label>

      <MessageError
        message={errorMessage}
        icon={errorIcon}
        state={state}
        theme={theme}
        className={errorClassName}
        errorSpace={errorSpace}
      />

      <MessageHelper message={helperMessage} state={state} theme={theme} />
    </div>
  );
}

export default InputDropdown;
